import type { DateTime } from 'luxon';

import type { Category } from 'constants/categories';
import type { DeterminationType } from 'constants/determination';
import type { ColorsPalette } from 'models/colorsPalette';

import type { EventVo } from './event';

/**
 * Normalized game-stoppage vocabulary stamped by the backend alongside the
 * raw provider status (e.g. SR's `wdelay` becomes `weather_delay`). Present
 * only while the game is stopped. Every value except `postponed` means the
 * game is still live today with a meaningful frozen score; `postponed` means
 * nothing is being played.
 */
export type MilestoneLiveDataStoppageStatus = 'weather_delay' | 'facility_delay' | 'delay' | 'suspended' | 'postponed';

/** Status-row labels for each stoppage kind (Overline row renders uppercase). */
export const stoppageStatusLabels: Record<MilestoneLiveDataStoppageStatus, string> = {
    weather_delay: 'WEATHER DELAY',
    facility_delay: 'FACILITY DELAY',
    delay: 'DELAYED',
    suspended: 'SUSPENDED',
    postponed: 'Postponed',
};

/** Whether a stoppage still counts as a live game (frozen board stays up). */
export function isInGameStoppage(stoppage: MilestoneLiveDataStoppageStatus | undefined): boolean {
    return !!stoppage && stoppage !== 'postponed';
}

/**
 * Status labels for a game that is over without a result. `cancelled` is
 * SportRadar's status for an abandoned cricket match, with or without play.
 * `abandoned` means the provider's feed ended without a result it can report:
 * for Decimal cricket, an ejection, a completion that lost its scoreboard, or
 * a fixture closed before play. That is not always a real abandonment
 * (Hampshire v Sussex, 2026-09-24, was played and won), so it gets a neutral
 * label. Either way the game is not upcoming, and it has no winner.
 */
export const noResultStatusLabels: Record<string, string> = {
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    abandoned: 'Result unavailable',
};

/** Whether a live-data status says the game is over without a result (see `noResultStatusLabels`). */
export function isNoResultStatus(status: string | undefined): boolean {
    return !!status && Object.hasOwn(noResultStatusLabels, status);
}

export interface SportsGame {
    type: MilestoneType;
    widgetLiveText: string;
    tileLiveText: string;
    isLiveDataDelayed?: boolean;
    /** Normalized stoppage kind while the game is paused (rain delay etc). */
    stoppage?: MilestoneLiveDataStoppageStatus;
    home_points: number;
    away_points: number;
    home_abbreviation: string;
    away_abbreviation: string;
    home_name: string;
    away_name: string;
    home_short_name: string;
    away_short_name: string;
    home_image_url: string;
    home_image_url_light?: string;
    home_image_url_dark?: string;
    home_secondary_image_url?: string;
    away_image_url: string;
    away_image_url_light?: string;
    away_image_url_dark?: string;
    away_secondary_image_url?: string;
    home_id: string;
    away_id: string;
    /** Sportradar team ids — the id space live pbp feeds key `possession` by. */
    home_sportradar_id?: string;
    away_sportradar_id?: string;
    home_rank?: number | string;
    away_rank?: number | string;
    status?: 'none' | 'live' | 'finished';
    postponed?: boolean;
    /** Set when the game is over without a result (`Cancelled`, `Result unavailable`); see `noResultStatusLabels`. */
    noResultLabel?: string;
    scheduled: string;
    round?: number;
    final_round_time_left?: string;
    winner?: string;
    home_is_first?: boolean;

    // Football
    leg?: string;
    possession?: 'home' | 'away';
    last_play: string;
    sub_title: string;
    /** 0-100 yards from the *home* goal line; see `utils/footballSituation.ts`. */
    yards_from_home_goal?: number;
    /**
     * The line to gain on the same scale; absent when no down is in progress
     * (kickoffs, tries). See `firstDownYardsFromHomeGoal`.
     */
    first_down_yards_from_home_goal?: number;
    /**
     * Timeouts left in the half, or the overtime stretch, while the game is
     * live or a chart scrub replays a moment of it. Undefined for a final at
     * rest and when the feed has no count (see `MilestoneLiveDataDetails`);
     * 0 means the team has spent them all.
     */
    home_timeouts_remaining?: number;
    away_timeouts_remaining?: number;
    /**
     * Timeouts each team was allotted for that stretch — the dots the indicator
     * draws. Three in regulation; overtime depends on the league, see
     * `footballTimeoutAllotment`. Set whenever the counts above can be.
     */
    football_timeouts_allotted?: number;

    // Soccer
    aggregate_text?: string;
    show_penalties?: boolean;
    home_penalties?: string[];
    away_penalties?: string[];
    home_penalties_score?: number;
    away_penalties_score?: number;
    home_significant_events?: SoccerSignificantEvent[];
    away_significant_events?: SoccerSignificantEvent[];

    // Tenis
    advantage?: string;
    server?: string;
    home_current_round_score?: string;
    home_seed?: number;
    home_round_scores?: TennisRoundScores[];
    home_tennis_statistics?: TennisPlayerStatistics;
    home_country?: string;
    away_current_round_score?: string;
    away_seed?: number;
    away_round_scores?: TennisRoundScores[];
    away_tennis_statistics?: TennisPlayerStatistics;
    away_country?: string;
    last_set_scoring_type?: TennisLastSetScoringType;
    last_set_tiebreak_points_to_win?: number;
    // Abnormal-end marker from SR (retired / walkover / withdrawn). Empty for
    // standard endings. The retirer is the player who isn't `winner`.
    match_status?: string;

    // Esports — each match is a best-of-N series of games/maps
    // home_game_scores: per-game detail stat (CS2 = rounds won, LoL/Dota = kills)
    // home_points is used for the series score (games/maps won)
    home_game_scores?: EsportsGameScore[];
    away_game_scores?: EsportsGameScore[];
    // Round-based games (CS2) show per-map round scores for completed maps because
    // individual round counts are meaningful context. Objective-based games (LoL/Dota)
    // show only the series score for completed maps because past-game kill totals are
    // not useful at a glance — the series standing matters more.
    esportsRoundBased?: boolean;
    // Unit for the live game's score label ("G2 kills"); undefined for titles without one.
    esportsGameScoreUnit?: 'rounds' | 'kills';
    // Sports-sim titles (eBasketball, eFootball): one game scored in points with a per-period
    // breakdown, rendered like a points sport rather than a best-of series.
    esportsSportsSim?: boolean;
    // OpticOdds reports live in-game scores for LoL only (not CS2/Dota).
    esportsHasLiveGameData?: boolean;
    // Series format from live data, e.g. "BO2", "BO3", "BO5"
    format?: string;
    // Duration of the current in-progress game/map (seconds)
    currentGameDurationSeconds?: number;
    // Per-player K/D stats (series totals + per-game breakdown)
    playerStats?: EsportsPlayerStats;
    // Aggregate team K/D stats (series totals + per-game breakdown)
    seriesStats?: EsportsSeriesStats;

    // Golf
    leaderboard?: MilestoneLeaderboardVo[];

    // MMA
    methodOfVictory?: string;
    fightLength: number;

    // Racing
    completedLaps?: number;
    totalLaps?: number;
    weather?: string;
    substatus?: string;
    airTemperature?: number;
    humidity?: number;

    // Rugby / AFL
    score_breakdown_labels?: string[];
    home_score_breakdown?: number[];
    away_score_breakdown?: number[];

    // Baseball
    balls?: number;
    strikes?: number;
    outs?: number;
    inning?: number;
    inning_half?: number;

    // Cricket
    batting?: 'home' | 'away' | 'innings_break';
    home_overs?: number;
    away_overs?: number;
    home_wickets?: number;
    away_wickets?: number;
    bases?: boolean[];

    // Table tennis (ITTF team match): home_points / away_points carry
    // sub-matches won. sub_matches lists the individual matches with their
    // own games-won and per-game points.
    sub_matches?: TableTennisSubMatch[];

    // True for head-to-head sports (tennis / darts / squash / Oddin table
    // tennis) rendered by the two-competitor box score. Distinguishes Oddin
    // 1v1 table tennis from SR team-tie table tennis (same milestone type).
    two_competitor?: boolean;

    // Darts / Squash / Volleyball — best-of-N matches rendered via the
    // shared TennisBody (period scores projected into home/away round scores).
    round_name?: string;
    competition_name?: string;
    best_of_sets?: number;
    best_of_legs?: number;
    // Darts only.
    home_darts_statistics?: DartsPlayerStatistics;
    away_darts_statistics?: DartsPlayerStatistics;
}

export enum MilestoneType {
    BASKETBALL_GAME = 'basketball_game',
    FOOTBALL_GAME = 'football_game',
    GOLF_TOURNAMENT = 'golf_tournament',
    GOLF_MATCH = 'golf_match',
    SOCCEER_TOURNAMENT_MULTI_LEG = 'soccer_tournament_multi_leg',
    TENIS_TOURNAMENT_SINGLES = 'tennis_tournament_singles',
    TENIS_TOURNAMENT_DOUBLES = 'tennis_tournament_doubles',
    TABLE_TENNIS_MATCH = 'table_tennis_match',
    MMA_MATCH = 'mma_match',
    BOXING_MATCH = 'boxing_match',
    RACING_TOURNAMENT = 'racing_tournament',
    CYCLING_RACE = 'cycling_race',
    BASEBALL_TOURNAMENT = 'baseball_tournament',
    BASEBALL_GAME = 'baseball_game',
    HOCKEY_TOURNAMENT = 'hockey_tournament',
    HOCKEY_MATCH = 'hockey_match',
    CRICKET_MATCH = 'cricket_match',
    E_SPORTS_MATCH = 'esports_match',
    RUGBY_MATCH = 'rugby_match',
    LACROSSE_MATCH = 'lacrosse_match',
    DARTS_MATCH = 'darts_match',
    SQUASH_MATCH = 'squash_match',
    VOLLEYBALL_MATCH = 'volleyball_match',
    AFL_MATCH = 'afl_match',
    POLITICAL_RACE = 'political_race',
    ONE_OFF_MILESTONE = 'one_off_milestone',
    COMPANY_REPORT = 'company_report',
    DECISION_DESK_MAYOR = 'decision-desk-mayor',
    DEBT_CLOCK = 'debt-clock',
    MENTION_EVENT = 'mention_event',
    ARTIST_STREAMS = 'artist_streams',
    TV_VIEWS = 'tv_views',
    TRUFLATION = 'truflation',
    UNKOWN = 'unknown',
}

const milestoneTypeValues: Set<string> = new Set(Object.values(MilestoneType));

export const isMilestoneType = (value: unknown): value is MilestoneType =>
    typeof value === 'string' && milestoneTypeValues.has(value);

export const isSoccerMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG].includes(type);
export const isTennisMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.TENIS_TOURNAMENT_SINGLES, MilestoneType.TENIS_TOURNAMENT_DOUBLES].includes(type);
// Hockey Infront streams are scoped to hockey_tournament: that is the type
// the backend enriches (KHL and Finnish Liiga league mappings); NHL is
// hockey_match and has no Infront property, so it must not pick up the
// Infront player paths.
export const isHockeyTournamentMilestone = (type?: MilestoneType) => type === MilestoneType.HOCKEY_TOURNAMENT;
// Milestone types whose livestreams come from Infront Bettor Video: the
// backend attaches `live_data.details.stream` when a booked event matches
// (ITF tennis; mapped soccer leagues; CPL cricket; KHL/Liiga hockey; mapped
// international basketball leagues) and the client mints a per-view playback
// URL via the stream_token endpoint — vs. esports' shared `url` in live_data.
// Basketball covers every basketball_game, but the backend only attaches a
// stream for its mapped leagues, so NBA/WNBA/college games simply never carry
// one.
export const isInfrontStreamMilestone = (type?: MilestoneType) =>
    isTennisMilestone(type) ||
    isSoccerMilestone(type) ||
    isCricketMilestone(type) ||
    isHockeyTournamentMilestone(type) ||
    isBasketballMilestone(type);
// Statsig experiment gating the livestream UI for an Infront-streamed
// milestone. Tennis is fully shipped (itf_tennis_livestream reached its
// decision) so it is not experiment-gated at all — null bypasses the check.
// Soccer, cricket, hockey, and basketball measure via sports_livestream (100%
// allocation, 50/50 enabled) so stream impact on trading activity is
// readable.
export const infrontLivestreamExperimentName = (type?: MilestoneType): string | null =>
    isSoccerMilestone(type) ||
    isCricketMilestone(type) ||
    isHockeyTournamentMilestone(type) ||
    isBasketballMilestone(type)
        ? 'sports_livestream'
        : null;
export const isTableTennisMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.TABLE_TENNIS_MATCH].includes(type);
export const isGolfMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.GOLF_TOURNAMENT, MilestoneType.GOLF_MATCH].includes(type);
export const isFootballMilestone = (type?: MilestoneType) => !!type && [MilestoneType.FOOTBALL_GAME].includes(type);
export const isBasketballMilestone = (type?: MilestoneType) => !!type && [MilestoneType.BASKETBALL_GAME].includes(type);
export const isRacingMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.RACING_TOURNAMENT, MilestoneType.CYCLING_RACE].includes(type);
export const isPoliticalRaceMilestone = (type?: MilestoneType) => type === MilestoneType.POLITICAL_RACE;
export const isTvViewsMilestone = (type?: MilestoneType) => type === MilestoneType.TV_VIEWS;
export const isBaseballMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.BASEBALL_TOURNAMENT, MilestoneType.BASEBALL_GAME].includes(type);
export const isHockeyMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.HOCKEY_TOURNAMENT, MilestoneType.HOCKEY_MATCH].includes(type);
export const isLiveMatchTrackerEligibleMilestoneType = (type?: MilestoneType) =>
    isSoccerMilestone(type) || isHockeyMilestone(type) || isBaseballMilestone(type);
export const isCricketMilestone = (type?: MilestoneType) => !!type && [MilestoneType.CRICKET_MATCH].includes(type);
export const isEsportsMilestone = (type?: MilestoneType) => !!type && [MilestoneType.E_SPORTS_MATCH].includes(type);
export const isDartsMilestone = (type?: MilestoneType) => !!type && [MilestoneType.DARTS_MATCH].includes(type);
export const isSquashMilestone = (type?: MilestoneType) => !!type && [MilestoneType.SQUASH_MATCH].includes(type);
export const isVolleyballMilestone = (type?: MilestoneType) =>
    !!type && [MilestoneType.VOLLEYBALL_MATCH].includes(type);
// Tennis/darts/squash: share the `competitor1_*` / `competitor2_*` live shape.
export const isTwoCompetitorMatch = (type?: MilestoneType) =>
    isTennisMilestone(type) || isDartsMilestone(type) || isSquashMilestone(type);
// Sports whose live feed ships a unified `period_scores` array (vs tennis's
// per-competitor round-score arrays). Distinct from `isTwoCompetitorMatch`:
// tennis is also two-competitor but is NOT period-feed.
export const isPeriodScoreMatch = (type?: MilestoneType) => isDartsMilestone(type) || isSquashMilestone(type);
export const isLacrosseMilestone = (type?: MilestoneType) => !!type && [MilestoneType.LACROSSE_MATCH].includes(type);
export const isRugbyMilestone = (type?: MilestoneType) => !!type && [MilestoneType.RUGBY_MATCH].includes(type);
export const isAflMilestone = (type?: MilestoneType) => !!type && [MilestoneType.AFL_MATCH].includes(type);
export const isOneOffMilestone = (type?: MilestoneType) => !!type && [MilestoneType.ONE_OFF_MILESTONE].includes(type);
export const isTruflationMilestone = (
    // Accepts both the domain `Milestone` (details.provider) and a raw
    // `MilestoneVo` — only `provider` is read, which both detail shapes share.
    milestone?: { type?: MilestoneType; details?: { provider?: string } } | null,
): boolean => {
    if (!milestone) return false;
    if (milestone.type === MilestoneType.TRUFLATION) return true;
    // TODO: remove this one_off + provider check once all truflation milestones use MilestoneType.TRUFLATION.
    return milestone.type === MilestoneType.ONE_OFF_MILESTONE && milestone.details?.provider === 'truflation';
};
export const isMentionEventMilestone = (type?: MilestoneType) => !!type && [MilestoneType.MENTION_EVENT].includes(type);

// Milestone fight type checkers
export const isMMAMilestone = (type?: MilestoneType) => !!type && [MilestoneType.MMA_MATCH].includes(type);
export const isBoxingMilestone = (type?: MilestoneType) => !!type && [MilestoneType.BOXING_MATCH].includes(type);
export const isFightMilestone = (type?: MilestoneType) => isMMAMilestone(type) || isBoxingMilestone(type);

const sportsMilestoneTypes: ReadonlySet<MilestoneType> = new Set([
    MilestoneType.BASKETBALL_GAME,
    MilestoneType.FOOTBALL_GAME,
    MilestoneType.GOLF_TOURNAMENT,
    MilestoneType.GOLF_MATCH,
    MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG,
    MilestoneType.TENIS_TOURNAMENT_SINGLES,
    MilestoneType.TENIS_TOURNAMENT_DOUBLES,
    MilestoneType.TABLE_TENNIS_MATCH,
    MilestoneType.MMA_MATCH,
    MilestoneType.BOXING_MATCH,
    MilestoneType.RACING_TOURNAMENT,
    MilestoneType.CYCLING_RACE,
    MilestoneType.BASEBALL_TOURNAMENT,
    MilestoneType.BASEBALL_GAME,
    MilestoneType.HOCKEY_TOURNAMENT,
    MilestoneType.HOCKEY_MATCH,
    MilestoneType.CRICKET_MATCH,
    MilestoneType.RUGBY_MATCH,
    MilestoneType.LACROSSE_MATCH,
    MilestoneType.E_SPORTS_MATCH,
    MilestoneType.AFL_MATCH,
    MilestoneType.DARTS_MATCH,
    MilestoneType.SQUASH_MATCH,
    MilestoneType.VOLLEYBALL_MATCH,
]);

export const isSportsMilestoneType = (type?: MilestoneType): boolean => !!type && sportsMilestoneTypes.has(type);

export const milestoneHasLiveData = (type?: MilestoneType) =>
    !!type &&
    [
        MilestoneType.BASKETBALL_GAME,
        MilestoneType.FOOTBALL_GAME,
        MilestoneType.GOLF_TOURNAMENT,
        MilestoneType.GOLF_MATCH,
        MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG,
        MilestoneType.TENIS_TOURNAMENT_SINGLES,
        MilestoneType.TENIS_TOURNAMENT_DOUBLES,
        MilestoneType.MMA_MATCH,
        MilestoneType.RACING_TOURNAMENT,
        MilestoneType.CYCLING_RACE,
        MilestoneType.BASEBALL_TOURNAMENT,
        MilestoneType.BASEBALL_GAME,
        MilestoneType.HOCKEY_TOURNAMENT,
        MilestoneType.HOCKEY_MATCH,
        MilestoneType.CRICKET_MATCH,
        MilestoneType.RUGBY_MATCH,
        MilestoneType.LACROSSE_MATCH,
        MilestoneType.E_SPORTS_MATCH,
        MilestoneType.AFL_MATCH,
        MilestoneType.DARTS_MATCH,
        MilestoneType.SQUASH_MATCH,
        MilestoneType.VOLLEYBALL_MATCH,
    ].includes(type);

export const supportedMilestoneTypes = [
    MilestoneType.BASKETBALL_GAME,
    MilestoneType.FOOTBALL_GAME,
    MilestoneType.GOLF_TOURNAMENT,
    MilestoneType.GOLF_MATCH,
    MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG,
    MilestoneType.TENIS_TOURNAMENT_SINGLES,
    MilestoneType.TENIS_TOURNAMENT_DOUBLES,
    MilestoneType.TABLE_TENNIS_MATCH,
    MilestoneType.MMA_MATCH,
    MilestoneType.BOXING_MATCH,
    MilestoneType.RACING_TOURNAMENT,
    MilestoneType.CYCLING_RACE,
    MilestoneType.BASEBALL_TOURNAMENT,
    MilestoneType.BASEBALL_GAME,
    MilestoneType.HOCKEY_TOURNAMENT,
    MilestoneType.HOCKEY_MATCH,
    MilestoneType.CRICKET_MATCH,
    MilestoneType.RUGBY_MATCH,
    MilestoneType.LACROSSE_MATCH,
    MilestoneType.ONE_OFF_MILESTONE,
    MilestoneType.E_SPORTS_MATCH,
    MilestoneType.DARTS_MATCH,
    MilestoneType.SQUASH_MATCH,
    MilestoneType.VOLLEYBALL_MATCH,
    MilestoneType.AFL_MATCH,
];
export const rankedMilestoneTypes = [
    MilestoneType.GOLF_TOURNAMENT,
    MilestoneType.GOLF_MATCH,
    MilestoneType.RACING_TOURNAMENT,
    MilestoneType.CYCLING_RACE,
];
// Milestone types whose markets stay open across multiple days, so the
// store loader shouldn't drop them just because start_date is past
// `isTodayOrYesterday`. Golf tournaments span four days; artist_streams
// and tv_views run a Fri-Thu Luminate trading week. Without these
// entries, opening a luminate event on Sun/Mon/Tue/Wed/Thu would silently
// fail to load its milestone, breaking every chart that depends on it.
export const milestonesThatCanTakeDays = [
    MilestoneType.GOLF_TOURNAMENT,
    MilestoneType.GOLF_MATCH,
    MilestoneType.CYCLING_RACE,
    MilestoneType.ARTIST_STREAMS,
    MilestoneType.TV_VIEWS,
];
export const milestoneTypeToDeterminationType = (type?: MilestoneType): DeterminationType | undefined => {
    if (!type) {
        return undefined;
    }
    switch (type) {
        case MilestoneType.BASKETBALL_GAME:
            return 'basketball';
        case MilestoneType.FOOTBALL_GAME:
            return 'football';
        case MilestoneType.GOLF_TOURNAMENT:
        case MilestoneType.GOLF_MATCH:
            return 'golf';
        case MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG:
            return 'soccer';
        case MilestoneType.TENIS_TOURNAMENT_SINGLES:
        case MilestoneType.TENIS_TOURNAMENT_DOUBLES:
            return 'tennis';
        case MilestoneType.TABLE_TENNIS_MATCH:
            return 'table_tennis';
        case MilestoneType.MMA_MATCH:
            return 'mma';
        case MilestoneType.BOXING_MATCH:
            return 'boxing';
        case MilestoneType.RACING_TOURNAMENT:
        case MilestoneType.CYCLING_RACE:
            return 'racing';
        case MilestoneType.BASEBALL_TOURNAMENT:
        case MilestoneType.BASEBALL_GAME:
            return 'baseball';
        case MilestoneType.HOCKEY_TOURNAMENT:
        case MilestoneType.HOCKEY_MATCH:
            return 'hockey';
        case MilestoneType.CRICKET_MATCH:
            return 'cricket';
        case MilestoneType.RUGBY_MATCH:
            return 'rugby';
        case MilestoneType.LACROSSE_MATCH:
            return 'lacrosse';
        case MilestoneType.AFL_MATCH:
            return 'afl';
        default:
            return undefined;
    }
};
export const milestoneTypeToLabel = (type?: MilestoneType): string => {
    if (!type) {
        return '';
    }
    switch (type) {
        case MilestoneType.BASKETBALL_GAME:
            return 'Basketball';
        case MilestoneType.FOOTBALL_GAME:
            return 'Football';
        case MilestoneType.GOLF_TOURNAMENT:
        case MilestoneType.GOLF_MATCH:
            return 'Golf';
        case MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG:
            return 'Soccer';
        case MilestoneType.TENIS_TOURNAMENT_SINGLES:
        case MilestoneType.TENIS_TOURNAMENT_DOUBLES:
            return 'Tennis';
        case MilestoneType.TABLE_TENNIS_MATCH:
            return 'Table Tennis';
        case MilestoneType.MMA_MATCH:
            return 'MMA';
        case MilestoneType.BOXING_MATCH:
            return 'Boxing';
        case MilestoneType.RACING_TOURNAMENT:
            return 'Racing';
        case MilestoneType.CYCLING_RACE:
            return 'Cycling';
        case MilestoneType.BASEBALL_TOURNAMENT:
        case MilestoneType.BASEBALL_GAME:
            return 'Baseball';
        case MilestoneType.HOCKEY_TOURNAMENT:
            return 'Hockey';
        case MilestoneType.CRICKET_MATCH:
            return 'Cricket';
        case MilestoneType.RUGBY_MATCH:
            return 'Rugby';
        case MilestoneType.LACROSSE_MATCH:
            return 'Lacrosse';
        case MilestoneType.E_SPORTS_MATCH:
            return 'Esports';
        case MilestoneType.DARTS_MATCH:
            return 'Darts';
        case MilestoneType.SQUASH_MATCH:
            return 'Squash';
        case MilestoneType.VOLLEYBALL_MATCH:
            return 'Volleyball';
        case MilestoneType.AFL_MATCH:
            return 'Aussie Rules';
        default:
            return '';
    }
};

export const milestoneTypeToLabelWithFallback = (
    type: MilestoneType | string,
    fallback: (type: string) => string,
): string => {
    if (isMilestoneType(type)) {
        return milestoneTypeToLabel(type);
    }
    return fallback(type);
};
export const milestoneTypeFromTag = (tag: string): MilestoneType | undefined => {
    switch (tag) {
        case 'Basketball':
            return MilestoneType.BASKETBALL_GAME;
        case 'Football':
            return MilestoneType.FOOTBALL_GAME;
        case 'Baseball':
            return MilestoneType.BASEBALL_GAME;
        case 'Hockey':
            return MilestoneType.HOCKEY_TOURNAMENT;
        case 'Tennis':
            return MilestoneType.TENIS_TOURNAMENT_SINGLES;
        case 'Table Tennis':
            return MilestoneType.TABLE_TENNIS_MATCH;
        case 'Golf':
            return MilestoneType.GOLF_TOURNAMENT;
        case 'Soccer':
            return MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG;
        case 'Racing':
            return MilestoneType.RACING_TOURNAMENT;
        case 'Cycling':
            return MilestoneType.CYCLING_RACE;
        case 'MMA':
            return MilestoneType.MMA_MATCH;
        case 'Boxing':
            return MilestoneType.BOXING_MATCH;
        case 'Cricket':
            return MilestoneType.CRICKET_MATCH;
        case 'Rugby':
            return MilestoneType.RUGBY_MATCH;
        case 'Lacrosse':
            return MilestoneType.LACROSSE_MATCH;
        case 'Volleyball':
            return MilestoneType.VOLLEYBALL_MATCH;
        case 'Esports':
            return MilestoneType.E_SPORTS_MATCH;
        case 'AFL':
        case 'Aussie Rules':
            return MilestoneType.AFL_MATCH;
        default:
            return undefined;
    }
};

export const milestoneTypeFromTagWithFallback = (
    tag: string,
    fallback: (tag: string) => string,
): MilestoneType | string => {
    return milestoneTypeFromTag(tag) ?? fallback(tag);
};

/**
 * `details.election_location` on Elections milestones (schema version 1). It keeps
 * apart which places vote (`subdivision_codes`, `places`), where the map dot goes
 * (`map_point`) and what kind of vote it is (`level`, `kind`). Codes are ISO 3166.
 * Market Ops tooling validates it on write; the server does not.
 */
export interface ElectionLocationVo {
    schema_version?: number;
    /** ISO 3166-1 alpha-2. */
    country_code: string;
    /** The level of government elected or voting. */
    level: 'national' | 'regional' | 'local';
    /** What is being decided. */
    kind: 'election' | 'referendum' | 'leadership';
    /** ISO 3166-2 codes of the states or provinces that vote, or that contain the places that vote. */
    subdivision_codes?: string[];
    /** The cities or seats that vote, when there are a handful. */
    places?: { name: string; wikidata?: string; lat: number; lng: number }[];
    /** Where the dot goes when the vote isn't exactly one country, subdivision or place (WGS 84). */
    map_point?: { lat: number; lng: number };
}

// Milestones API model
export interface MilestoneDetailsVo {
    source?: string;
    provider?: string;
    tour?: string;
    round?: string;
    main_game_event_ticker?: string;
    /** Series ticker → box-score stat key (e.g. KXNFLRECYDS → receiving_yards); also mirrored on `product_details`. */
    series_to_stat_mapping?: Record<string, string>;

    /**
     * Season context on scheduled games, e.g. `{ type: 'REG', week: 1, year: 2026 }`
     * on NFL games. Numerics are widened to `number | string` because the
     * milestone payload stringifies some numeric fields.
     */
    season?: {
        /**
         * Season phase. Providers disagree on the postseason code — Sportradar
         * writes `PST`, other feeds `POST` — so consumers must accept both; see
         * `SEASON_PHASE_CODES` in `utils/seasonPhase`.
         */
        type?: string;
        /** Schedule week within the phase. */
        week?: number | string;
        /** Season year. */
        year?: number | string;
    };

    // MilestoneType.GOLF_TOURNAMENT, MilestoneType.RACING_TOURNAMENT
    competitor_ids?: string[];

    // MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG
    home_team_id?: string;
    away_team_id?: string;
    tie_id?: string;
    league?: string;
    leg?: string;

    // MilestoneType.TENIS_TOURNAMENT_SINGLES
    first_competitor_id?: string;
    second_competitor_id?: string;
    gender?: 'women' | 'men';
    tournament_name?: string;

    // MilestonType.TABLE_TENNIS_MATCH
    home_player_id?: string;
    away_player_id?: string;

    // MilestonType.MMA_MATCH
    promotion?: string;
    first_fighter_id?: string;
    second_fighter_id?: string;
    weight_class?: string;
    event?: string;
    fight_length?: number;

    // MilestoneType.E_SPORTS_MATCH
    home_competitor_id?: string;
    away_competitor_id?: string;
    game?: string; // e.g. "CS2", "League of Legends", "Dota 2"

    // MilestoneType.POLITICAL_RACE
    reporting_percentage?: string;
    candidates?: Record<string, PoliticalRaceCandidateResult>;
    state?: string;
    state_postal?: string;

    // Elections milestones (international ones carry both on every milestone)
    /** Short name for chips and map labels, e.g. "BC municipal"; recurring milestones use it too. */
    display_name?: string;
    election_location?: ElectionLocationVo;

    // Company KPI (one_off_milestone with Companies category)
    metric_id?: string;
    metric_id_v2?: string;
    metric_name?: string;
    ticker?: string;
    quarter?: string;

    // MilestoneType.ARTIST_STREAMS
    competition?: string;
    artist?: string;
    // Artist-vs variant: two-artist head-to-head milestones carry names for
    // each side here and luminate IDs in the product_details payload.
    artist_1?: string;
    artist_1_id?: string;
    artist_2?: string;
    artist_2_id?: string;
}

/** Series ticker → box-score stat key; the backend mirrors it on `details` and `product_details`. */
export function milestoneSeriesToStatMapping(
    milestone: Pick<MilestoneVo, 'details' | 'product_details'> | null | undefined,
): Record<string, string> | undefined {
    return milestone?.details?.series_to_stat_mapping ?? milestone?.product_details?.series_to_stat_mapping;
}

export interface MilestoneProductDetailsVo {
    combo_controls?: { creation_blocked_structured_target_ids?: string[] };
    series_to_stat_mapping?: Record<string, string>;
    sportradar_id: string;
    sr_id?: string;
    youtube_link?: string;
    home_is_first?: boolean;
    series_win_loss_record?: string;
    chip?: string;
    disclaimer?: string;
    postponed?: boolean;
    /** Ops-set kill switch — do not fetch or display live data. */
    live_data_disabled?: boolean | string;
    /** Ops-set flag (market-ops dashboard) — show the "data delayed" indicator.
     *  Written by hand in admin tooling, so both `true` and `"true"` appear. */
    live_data_delayed?: boolean | string;
    grid_series_id?: number | string;
    last_week_count?: number;
    date_range?: string;
    series_game_number?: string;
    metric_key?: string;

    // MilestoneType.POLITICAL_RACE
    election_day?: string;
    state?: string;
    state_postal?: string;
    /** Congressional district number within the state (e.g. "6"), set on
     *  district-level (House) races. */
    district?: string;
    /** Votehub race identifier (e.g. "s_2026_nh_02"). When set, the milestone
     *  live-data payload carries a `votehub` block (see `VotehubRaceData`) with
     *  polling averages and FEC fundraising data. */
    votehub_id?: string;
    votehub_title?: string;
    /** Declared candidate-name → FEC candidate id map (e.g.
     *  {"Cait Conley": "H6NY17171"}). The polling-independent identity source
     *  for FEC fundraising data: races without a Votehub polling average (no
     *  `votehub_id`) still carry a `votehub` block with `averages: null` and
     *  fundraising under `fec`. */
    fec_ids?: Record<string, string>;
}

export interface MilestoneProductDetailsDerivedVo {
    secondary_title?: string;
    tertiary_title?: string;
}

export interface MilestoneVo {
    id: string;
    category: Category;
    type: MilestoneType;
    start_date: string;
    end_date?: string;
    related_event_tickers: string[];
    title: string;
    notification_message: string;
    details: MilestoneDetailsVo;
    product_details: MilestoneProductDetailsVo;
    product_details_derived: MilestoneProductDetailsDerivedVo;
    primary_event_tickers: string[];
    last_updated_ts: string;
    competition?: string;
    competition_scope?: string;
}

export enum MilestoneLiveDataStatus {
    CREATED = 'created',
    SCHEDULED = 'scheduled',
    NOT_STARTED = 'Not started',
    STARTED = 'started',
    IN_PROGRESS = 'inprogress',
    DELAYED = 'delayed',
    LIVE = 'live',
    HALF_TIME = 'halftime',
    INTERRUPTED = 'interrupted',
    ENDED = 'ended',
    CLOSED = 'closed',
    COMPLETE = 'complete',
}

export const shouldPollMilestoneStatuses = [
    MilestoneLiveDataStatus.DELAYED,
    MilestoneLiveDataStatus.SCHEDULED,
    MilestoneLiveDataStatus.NOT_STARTED,
    MilestoneLiveDataStatus.STARTED,
    MilestoneLiveDataStatus.CREATED,
    MilestoneLiveDataStatus.IN_PROGRESS,
    MilestoneLiveDataStatus.HALF_TIME,
    MilestoneLiveDataStatus.LIVE,
    MilestoneLiveDataStatus.INTERRUPTED,
];

export const liveMilestoneStatuses = [
    MilestoneLiveDataStatus.STARTED,
    MilestoneLiveDataStatus.IN_PROGRESS,
    MilestoneLiveDataStatus.LIVE,
    MilestoneLiveDataStatus.HALF_TIME,
    MilestoneLiveDataStatus.INTERRUPTED,
];

export const endedMilestoneStatuses = [
    MilestoneLiveDataStatus.ENDED,
    MilestoneLiveDataStatus.CLOSED,
    MilestoneLiveDataStatus.COMPLETE,
];

export const startingSoonMilestoneStatuses = [MilestoneLiveDataStatus.CREATED, MilestoneLiveDataStatus.NOT_STARTED];

export enum MilestoneLiveDataWidgetStatus {
    SCHEDULED = 'none',
    LIVE = 'live',
    FINISHED = 'finished',
    // The esports provider reports completion as 'complete' instead of 'finished'.
    COMPLETE = 'complete',
}

export interface MilestoneLeaderboardVo {
    competitor_id: string;
    position: number; // 1-based, 1 is the best

    // MilestoneType.CYCLING_RACE (included in the live feed so the
    // leaderboard can render without waiting for structured-target hydration)
    name?: string;
    team_name?: string;
    country_code?: string;
    sprint_ranking?: number;
    climber_ranking?: number;
    young_rider_ranking?: number;

    // MilestoneType.GOLF_TOURNAMENT
    total_score?: number;
    started_current_round?: boolean;
    finished_current_round?: boolean;
    current_round_score?: number;
    current_round_thru?: number;

    // MilestoneType.RACING_TOURNAMENT
    time?: string;

    // Racing live-data enrichments (F1 + NASCAR)
    status?: string;
    team?: string;
    car_number?: number | string;
    fastest_lap_time?: string;
    grid?: number;
    laps?: number;
    pitstop_count?: number;

    // NASCAR-specific enrichments
    driver_name?: string;
    manufacturer?: string;
    laps_completed?: number;
    laps_led?: number;
    start_position?: number;
    pit_stop_count?: number;
    best_lap_time?: number;
    best_lap_speed?: number;
    avg_speed?: number;
    last_lap_speed?: number;
    points?: number;
}

export interface EsportsGameScore {
    outcome: 'winner' | 'loser' | 'ongoing';
    score: number; // rounds won (CS2) or kills (LoL/Dota)
    has_score?: boolean;
    map_duration_seconds?: number;
    stats?: Record<string, number>;
}

export interface EsportsPlayerStat {
    id: string;
    name: string;
    kills: number;
    deaths: number;
}

export interface EsportsPlayerStats {
    home: EsportsPlayerStat[];
    away: EsportsPlayerStat[];
    games?: Record<string, { home: EsportsPlayerStat[]; away: EsportsPlayerStat[] }>;
}

export interface EsportsTeamKD {
    kills: number;
    deaths: number;
}

export interface EsportsSeriesStats {
    home: EsportsTeamKD;
    away: EsportsTeamKD;
    games?: Record<string, { home: EsportsTeamKD; away: EsportsTeamKD }>;
}

export type TennisLastSetScoringType = 'StandardTiebreak' | 'MatchTiebreak' | 'Advantage';

export interface TennisRoundScores {
    outcome: 'winner' | 'loser' | 'ongoing';
    score: number;
    // Per-competitor tiebreak point count for sets that went to a tiebreak
    // (e.g. set ending 7-6 with TB 8-6 → tiebreak_score is 6 on the loser's
    // entry, 8 on the winner's). Absent on regular sets. Rendered as
    // superscript next to the games-won score.
    tiebreak_score?: number;
}

// Per-player darts totals from the SportRadar darts summary; all optional
// (upstream omits keys it has no value for).
export interface DartsPlayerStatistics {
    average_3_darts?: number;
    checkout_percentage?: number;
    checkouts?: number;
    checkouts_100s_plus?: number;
    highest_checkout?: number;
    scores_100s_plus?: number;
    scores_140s_plus?: number;
    scores_180s?: number;
}

// One period (set/game/leg) of live data. Darts/squash are competitor-oriented;
// Volleyball is home/away-oriented. `number` is 1-indexed and `type` is the
// verbatim SportRadar period type.
export interface PeriodScore {
    competitor1_score?: number;
    competitor2_score?: number;
    home_score?: number;
    away_score?: number;
    type?: string;
    number?: number;
}

export interface TennisPlayerStatistics {
    aces?: number;
    breakpoints_won?: number;
    double_faults?: number;
    first_serve_points_won?: number;
    first_serve_successful?: number;
    games_won?: number;
    max_games_in_a_row?: number;
    max_points_in_a_row?: number;
    points_won?: number;
    points_won_from_last_10?: number;
    second_serve_points_won?: number;
    second_serve_successful?: number;
    service_games_won?: number;
    service_points_lost?: number;
    service_points_won?: number;
    tiebreaks_won?: number;
    total_breakpoints?: number;
}

export type SoccerSignificantEventType = 'score_change' | 'yellow_card' | 'red_card';
export interface SoccerSignificantEvent {
    event_type: SoccerSignificantEventType;
    player: string;
    time: string;
}

export interface MilestoneLastPlayDetails {
    description?: string;
    occurence_ts?: number;
}

// One chapter of a Quartr call recording (e.g. "Prepared remarks", "Q&A"),
// in audio-stream seconds.
export interface CompanyReportEventChapter {
    title?: string;
    start_seconds?: number;
    end_seconds?: number;
}

// One corporate event on the Quartr calendar returned for a company_report
// milestone.
export interface CompanyReportEvent {
    quartr_event_id?: number;
    title?: string;
    type_id?: number;
    date?: string;
    fiscal_year?: number;
    fiscal_period?: string;
    live_state?: string;
    audio_stream_url?: string;
    transcript_stream_url?: string;
    went_live_at?: string;
    is_milestone_event?: boolean;
    summary?: string;
    summary_updated_at?: string;
    duration_seconds?: number;
    chapters?: CompanyReportEventChapter[];
}

// One game in a table tennis sub-match. Each game is first-to-11 (must win
// by 2) so deuce games can have scores like 12-10.
export interface TableTennisGame {
    home: number;
    away: number;
}

// One sub-match in a table tennis team tie. order is 1-indexed in
// chronological play order. Cancelled sub-matches (typically once a team
// has clinched the tie) carry status="cancelled" with no games[].
export interface TableTennisSubMatch {
    order: number;
    status?: string;
    match_status?: string;
    home_player_name?: string;
    away_player_name?: string;
    home_player_abbreviation?: string;
    away_player_abbreviation?: string;
    home_player_id?: string;
    away_player_id?: string;
    home_games_won?: number;
    away_games_won?: number;
    winner?: 'home' | 'away';
    winner_competitor_id?: string;
    games?: TableTennisGame[];
}

// One entry of live_data `streams`: Grid tokenized entries carry account_id;
// Oddin fallback entries are public provider URLs (Twitch/YouTube/Kick) with
// type "URL" and a language tag. Provider resolution lives in
// components/Graph/Multimedia/streamSource.ts.
export interface MilestoneLiveDataStreamEntry {
    name?: string;
    url?: string;
    type?: string;
    title?: string;
    language?: string;
    account_id?: string;
}

/**
 * Football game situation. Only `possession_team_id` is guaranteed: the
 * field-position members are absent before kickoff and between periods.
 *
 * `yardline` is 1-50 measured from `side_team_id`'s own goal line, so the pair
 * is needed to place the ball on the full 100-yard field — see
 * `yardsFromHomeGoal` in `utils/footballSituation.ts`.
 */
export interface FootballSituation {
    possession_team_id: string;
    side_team_id?: string;
    yardline?: number;
    down?: number;
    /** Yards for down, i.e. the distance to the line to gain. */
    yfd?: number;
    goal_to_go?: boolean;
}

export interface MilestoneLiveDataDetails {
    live_data_delayed?: boolean | string;
    status?: MilestoneLiveDataStatus;
    widget_status?: MilestoneLiveDataWidgetStatus;
    stoppage_status?: MilestoneLiveDataStoppageStatus;
    last_play?: string | MilestoneLastPlayDetails;
    current_round?: number; // Golf, MMA
    winner?: string; // Tenis, MMA, Racing
    leaderboard?: MilestoneLeaderboardVo[]; // Golf, Racing
    product_details?: {
        title: string;
    };
    home_points?: number;
    away_points?: number;

    // MilestoneType.COMPANY_REPORT (Quartr company report calendar).
    // latest_event / next_event are selected by svc-live-data around its
    // current clock; live_call is present only while a call is streaming.
    provider?: string;
    company_name?: string;
    quartr_company_id?: number;
    events?: CompanyReportEvent[];
    latest_event?: CompanyReportEvent;
    next_event?: CompanyReportEvent;
    live_call?: CompanyReportEvent;

    // Football
    quarter?: number; // 1-based, 1 is the first quarter
    clock?: string;
    situation?: FootballSituation;
    /**
     * Per-half timeouts remaining (SportRadar resets them at the break). The
     * feed sends an explicit `null` when it has no count — the scores-only
     * StatsPerform fallback always does — so 0 always means "none left".
     */
    home_timeouts_remaining?: number | null;
    away_timeouts_remaining?: number | null;

    // MilestoneType.GOLF_TOURNAMENT
    round_label?: string;
    total_rounds?: number;

    // MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG
    aggregate_text?: string;
    away_same_game_score?: number;
    away_aggregate_score?: number;
    away_penalties?: string[];
    home_same_game_score?: number;
    home_aggregate_score?: number;
    home_penalties?: string[];
    /** The shootout score; served with `show_penalties`, kick list or not. */
    home_penalties_score?: number;
    away_penalties_score?: number;
    /** The shootout score as "H-A". */
    penalties_text?: string;
    home_significant_events?: SoccerSignificantEvent[];
    away_significant_events?: SoccerSignificantEvent[];
    half?: string;
    show_penalties?: boolean;
    status_text?: string;
    time?: string;

    // Infront Bettor Video stream (ITF tennis, mapped soccer leagues, CPL
    // cricket, KHL/Liiga hockey, mapped international basketball leagues) —
    // see isInfrontStreamMilestone.
    stream?: {
        event_id: number;
        available?: boolean;
        hls_url?: string;
        low_latency_url?: string;
    };
    advantage?: string;
    away_overall_score?: number;
    home_overall_score?: number;
    competitor1_id?: string;
    competitor1_is_home?: boolean;
    competitor1_current_round_score?: number;
    competitor1_overall_score?: number;
    competitor1_seed?: number;
    // Tennis carries TennisPlayerStatistics; darts reuses this key with
    // DartsPlayerStatistics. Consumers narrow by milestone type and cast.
    competitor1_statistics?: TennisPlayerStatistics;
    competitor1_round_scores?: TennisRoundScores[];
    competitor2_id?: string;
    competitor2_is_home?: boolean;
    competitor2_current_round_score?: number;
    competitor2_overall_score?: number;
    competitor2_seed?: number;
    competitor2_statistics?: TennisPlayerStatistics;
    competitor2_round_scores?: TennisRoundScores[];
    last_set_scoring_type?: TennisLastSetScoringType;
    last_set_tiebreak_points_to_win?: number;
    completed_rounds?: number;
    round_winner?: string[]; // competitor id
    server?: string; // competitor id
    // SR's match_status (e.g. "retired", "walkover", "withdrawn") for abnormal
    // endings. Standard endings leave this empty / "ended".
    match_status?: string;

    // Darts/squash: scores under `period_scores` (one entry per set/leg);
    // names/abbreviations/country codes emitted inline.
    competitor1_name?: string;
    competitor1_abbreviation?: string;
    competitor1_country_code?: string;
    competitor2_name?: string;
    competitor2_abbreviation?: string;
    competitor2_country_code?: string;
    period_scores?: PeriodScore[];
    // `winner_competitor_id` (shared with table tennis, declared above).
    round_name?: string;
    competition_name?: string;
    best_of_sets?: number;
    best_of_legs?: number;
    best_of?: number;
    // For darts, `competitor1_statistics` above is DartsPlayerStatistics
    // (narrowed by milestone type at the consumption site).

    // MilestoneType.BASKETBALL_GAME
    period?: number;
    period_remaining_time?: string;
    possession?: 'home' | 'away';
    home_halves?: Record<string, number>;
    away_halves?: Record<string, number>;
    home_quarters?: Record<string, number>;
    away_quarters?: Record<string, number>;

    // MilestonType.MMA_MATCH
    final_round?: number;
    final_round_time_left?: string;
    method_of_victory?: string;

    // MilestoneType.RACING_TOURNAMENT
    completed_laps?: number;
    total_laps?: number;
    weather?: string;
    substatus?: string;
    air_temperature?: number;
    humidity?: number;

    // MilestoneType.BASEBALL_TOURNAMENT
    balls?: number;
    inning?: number;
    inning_half?: number;
    outs?: number;
    strikes?: number;

    // MilestoneType.E_SPORTS_MATCH
    // How home_score/away_score and *_periods read: 'series' is maps won with one
    // period per map; 'points' is a single game's total with points per period_unit.
    score_format?: 'series' | 'points';
    period_unit?: string; // 'map' | 'quarter' | 'half'
    home_periods?: Record<string, number>; // per-game detail stat (rounds/kills), or per-period points
    away_periods?: Record<string, number>;
    home_stats?: Array<{ period: string; stats: Record<string, any> }>;
    away_stats?: Array<{ period: string; stats: Record<string, any> }>;
    player_stats?: EsportsPlayerStats;
    series_stats?: EsportsSeriesStats;
    is_live?: boolean;
    url?: string; // primary stream URL: HLS manifest (Grid/BunnyCDN) or public provider page (Oddin)
    streams?: MilestoneLiveDataStreamEntry[]; // multi-provider/multi-language stream list
    is_offline?: boolean; // Grid stream-coordinator offline flag
    format?: string; // e.g. "BO2", "BO3", "BO5"
    clock_ticking?: boolean;
    duration_seconds?: number;
    current_game_duration_seconds?: number;

    // MilestoneType.CRICKET_MATCH (home_score/away_score also used by esports
    // and table tennis — see TABLE_TENNIS_MATCH section below)
    batting?: 'home' | 'away' | 'innings_break';
    home_score?: number;
    away_score?: number;
    home_overs?: number;
    away_overs?: number;
    home_wickets?: number;
    away_wickets?: number;
    bases?: boolean[];

    // MilestoneType.TABLE_TENNIS_MATCH (ITTF team tie). home_score/away_score
    // declared above are reused as sub-matches won (best-of-5 ties → first to
    // 3). sub_matches lists each individual match in chronological order with
    // its own games-won + per-game points. See svc-live-data table tennis
    // parser for the wire shape.
    home_abbreviation?: string;
    away_abbreviation?: string;
    home_country_code?: string;
    away_country_code?: string;
    sub_matches?: TableTennisSubMatch[];
    winner_competitor_id?: string;

    // MilestoneType.POLITICAL_RACE
    reporting_percentage?: string;
    candidates?: Record<string, PoliticalRaceCandidateResult>;
    // `winner` is declared above (shared with Tennis/MMA/Racing). For political
    // races it carries the candidate key — for binary referendums this is the
    // fixed Yes/No candidate UUID (`BINARY_YES_KEY` / `BINARY_NO_KEY` in
    // `usePoliticalRaceLiveData`).
    winners?: string[];
    race_call_status?: string;
    tabulation_status?: string;
    last_updated?: string;
    /** Polling averages + FEC fundraising for the race. Present when the
     *  milestone's product_details carries a `votehub_id`. Available well ahead
     *  of election day, unlike `candidates`/`reporting_percentage`, which are
     *  populated from the AP feed on election night. */
    votehub?: VotehubRaceData;
}

export interface PoliticalRaceCandidateResult {
    count: number;
    percentage: string;
    // Present on binary referendum races where candidates are keyed by the
    // fixed Yes/No UUIDs (`BINARY_YES_KEY` / `BINARY_NO_KEY` in
    // `usePoliticalRaceLiveData`) and carry a human-readable "Yes" / "No"
    // label.
    name?: string;
}

/** One polling-average data point from the Votehub feed. Per candidate
 *  (`series_key`) a race carries an `average` value plus `lower`/`upper`
 *  confidence-band values for a given `date`. */
export interface VotehubPollAverage {
    average_key: string;
    date: string;
    series_key: string;
    type: 'average' | 'lower' | 'upper';
    value: number;
}

/** FEC fundraising snapshot for one candidate in a Votehub race. Keyed in
 *  `VotehubRaceData.fec` by the candidate's display name (same key as the
 *  `series_key` in `VotehubPollAverage`). */
export interface VotehubFecCandidate {
    candidate?: {
        chamber?: string;
        comp_bin?: string;
        cycle?: number;
        district?: string;
        fec_id?: string;
        general_date?: string;
        ici?: string;
        name?: string;
        name_raw?: string;
        /** Full party code, e.g. "DEM", "REP". */
        party?: string;
        /** Single-letter party bucket, e.g. "D", "R", "I". */
        party_bucket?: string;
        primary_date?: string;
        state?: string;
        weekly_method?: string;
    };
    current?: {
        as_of?: string;
        cash_on_hand?: number;
        /** FEC report period, e.g. "Q1". */
        report_type?: string;
        total_disbursements_cycle?: number;
        total_receipts_cycle?: number;
    };
    donor_profile?: {
        itemized_total?: number;
        n_contributions?: number;
        out_of_state_share?: string;
        /** Share (0–1) of itemized contributions from small-dollar donors. */
        small_dollar_share?: string;
    };
}

export interface VotehubRaceData {
    /** Polling-average points, verbatim from polling.votehub.com. `null` for
     *  FEC-only races (declared `fec_ids`, no polling average). */
    averages?: VotehubPollAverage[] | null;
    fec?: Record<string, VotehubFecCandidate>;
}

export interface MilestoneLiveDataVo {
    milestone_id: string;
    type: MilestoneType;
    details: MilestoneLiveDataDetails;
    /**
     * True when Kalshi hosts a live video feed for this milestone that is
     * playable right now (the Willow CPL cricket feed today). Unlike the
     * Infront flag this lives at the top level, not under `details`, and is
     * resolved server-side from the milestone's series — clients must not
     * infer it from the ticker, or every new series would need a deploy.
     *
     * The flag only says a stream exists; the playable URL comes from
     * `/v1/live_data/milestone/:id/stream`, which also sets the CloudFront
     * cookies that authorize playback.
     */
    stream_available?: boolean;
}

/** Admin tooling writes live-data flags by hand, so accept both JSON booleans
 *  and "true" strings (mirrors the backend's boolFieldSet behavior). */
export const isLiveDataFlagSet = (value: unknown): boolean =>
    value === true || (typeof value === 'string' && value.toLowerCase() === 'true');

// Milestones Domain model
export interface MilestoneDetails {
    source?: string;
    provider?: string;
    competitorIds?: string[];
    tour?: string;
    homeTeamId?: string;
    awayTeamId?: string;
    tieId?: string;
    mainGameEventTicker?: string;

    // MilestoneType.SOCCEER_TOURNAMENT_MULTI_LEG
    league?: string;
    leg?: string;
    round?: string;

    // MilestoneType.TENIS_TOURNAMENT_SINGLES
    firstCompetitorId?: string;
    secondCompetitorId?: string;
    gender?: 'women' | 'men';
    tournamentName?: string;

    // MilestoneType.TABLE_TENNIS_MATCH
    homePlayerId?: string;
    awayPlayerId?: string;

    // MilestoneType.MMA_MATCH
    promotion?: string;
    firstFighterId?: string;
    secondFighterId?: string;
    weightClass?: string;
    event?: string;
    fightLength: number;

    // MilestoneType.E_SPORTS_MATCH
    game?: string; // e.g. "CS2", "League of Legends", "Dota 2"

    // Company KPI (one_off_milestone with Companies category)
    metricId?: string;
    metricIdV2?: string;
    ticker?: string;

    // MilestoneType.ARTIST_STREAMS
    competition?: string;
    artist?: string;
    // Artist-vs variant — head-to-head markets carry a name (+ structured
    // target id) for each of the two sides.
    artist1?: string;
    artist1Id?: string;
    artist2?: string;
    artist2Id?: string;
}

export interface MilestoneProductDetails {
    /** Sportradar match identifier used by the Live Match Tracker widget. */
    srId?: string;
    youtubeLink?: string;
    homeIsFirst?: boolean;
    seriesWinLossRecord?: string;
    chip?: string;
    disclaimer?: string;
    postponed?: boolean;
    /** See `MilestoneProductDetailsVo.live_data_delayed`. */
    liveDataDelayed?: boolean | string;
    gridSeriesId?: number | string;
    lastWeekCount?: number;
    dateRange?: string;
    metricKey?: string;

    // MilestoneType.POLITICAL_RACE
    electionDay?: string;
    state?: string;
    statePostal?: string;
    /** See `MilestoneProductDetailsVo.votehub_id`. */
    votehubId?: string;
    votehubTitle?: string;
}

export interface MilestoneLiveData {
    lastUpdate: DateTime;
    data: MilestoneLiveDataVo;
}

// Structured targets API model
export enum StructuredTargetType {
    SOCCER_TEAM = 'soccer_team',
    FOOTBALL_TEAM = 'football_team',
    BASKETBALL_TEAM = 'basketball_team',
    BASEBALL_TEAM = 'baseball_team',
    HOCKEY_TEAM = 'hockey_team',
    CRICKET_TEAM = 'cricket_team',
    UFC_COMPETITOR = 'ufc_competitor',
    RUGBY_TEAM = 'rugby_team',
    LACROSSE_TEAM = 'lacrosse_team',
    VOLLEYBALL_TEAM = 'volleyball_team',
    MUSICIAN = 'musician',
}

/**
 * Whether a structured-target `type` names a team (`football_team`,
 * `basketball_team`, …). The enum above undersells the wire format — player
 * types like `football_player` exist on responses — so classification goes by
 * the `_team`/`_player` suffix convention rather than enum membership.
 */
export const isTeamTargetType = (type: string | undefined): boolean => !!type?.endsWith('_team');

/** Whether a structured-target `type` names a player — see {@link isTeamTargetType}. */
export const isPlayerTargetType = (type: string | undefined): boolean => !!type?.endsWith('_player');

/**
 * A team's standing for one season+phase, as returned on
 * `structured_target.details.season_standings`. The backend only populates the
 * most recently *played* season, so an entry for the current year is absent
 * until that season's first result lands — see `formatTeamSeasonRecord`.
 */
interface StructuredTargetsSeasonStandings {
    year: number;
    /** Sportradar season phase: `REG` (regular), `PST`/`POST`, `PRE`. */
    season_type?: string;
    wins?: number;
    losses?: number;
    ties?: number;
    rank?: {
        ap25?: number | string;
        cfp?: number | string;
        clinched: number | string;
        conference: number | string;
        division: number | string;
    };
}

/**
 * Per-season group-stage standing for a team, keyed by season in `details.seasons`
 * (e.g. `wc_2026`). Populated by the backend for soccer competitions; numeric
 * fields may arrive as strings, so coerce at the read site (see
 * `getWorldCupGroupStanding`). All fields optional — pre-tournament the map may
 * be empty or carry only `group`.
 */
interface StructuredTargetSeasonStanding {
    group?: string;
    group_rank?: number | string;
    points?: number | string;
    goal_diff?: number | string;
    wins?: number | string;
    losses?: number | string;
    draws?: number | string;
    played?: number | string;
    goals_for?: number | string;
    goals_against?: number | string;
}

interface StructuredTargetDetailsVo {
    abbreviation: string;
    first_name: string;
    last_name: string;
    short_name: string;
    team?: string;
    country: string;
    country_code: string;
    gender?: 'male' | 'female';
    season_standings: StructuredTargetsSeasonStandings[];
    /** Governing league of a sports team, e.g. "NFL", "NCAAFB". */
    league?: string;
    /** Divisional grouping of a sports team, e.g. "AFC West", "FBS". */
    division?: string;
    /** Standings keyed by season (e.g. `wc_2026`). Present for soccer competitions. */
    seasons?: Record<string, StructuredTargetSeasonStanding>;
    team_id?: string;
    conference?: string;
    /** Home city/region of a sports team, e.g. "Philadelphia". */
    market?: string;
    /** Nickname of a sports team, e.g. "Eagles". */
    team_name?: string;
    /** Jersey number of a player target, e.g. "15". */
    jersey?: string;
    /** Party initial of a political-party target, e.g. "D". */
    initial?: string;
}

interface StructuredTargetProductDetailsVo {
    image_url: string;
    secondary_image_url?: string;
    official_image_url_dark?: string;
    official_image_url_light?: string;
    sportradar_id: string;
    color_palette?: string;
    'Color Palette'?: string;
    /** Marketing-facing team name, usually the home city, e.g. "Philadelphia". */
    commercial_team_name?: string;
}

export interface StructuredTargetVo {
    colors?: ColorsPalette;
    id: string;
    name: string;
    type: StructuredTargetType;
    details: StructuredTargetDetailsVo;
    product_details: StructuredTargetProductDetailsVo;
}

export interface Bracket {
    name: string;
    rounds: BracketRound[];
}

interface BracketRound {
    name: string;
    sections: BracketSection[];
}

export type BracketPosition = 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'center';

interface BracketSection {
    name: string;
    position: BracketPosition;
    entries: BracketEntry[];
}

export interface BracketEntry {
    id: string;
    milestone_id: string;
    parent_entry_ids: Array<string | null>;
    event_ticker: string;
    market_tickers: string[];
}

export interface HydratedDataVo {
    milestones: Map<string, MilestoneVo>;
    structured_targets: Record<string, StructuredTargetVo>;
    events: Map<string, EventVo>;
}
